import { useTranslation } from 'react-i18next';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { I18N_KEYS } from '@/language/keys';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { useUserWorkflowRole } from '@/state/workflow.state';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import type { MenuAction } from '@/components/Menu/Actions';
import type { RenderingAsset, MediaAsset, TextAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

interface GalleryMenuProps {
  contextMenu: {
    mouseX: number;
    mouseY: number;
  } | null;
  selectedFile: MediaAsset | TextAsset | RenderingAsset | FileKind | undefined;
  rightClickedIndex: number | null;
  handleContextMenuClose: () => void;
  handleDownload: (index: number) => Promise<void>;
  handleDownloadAll: () => Promise<void>;
  handleDownloadImage?: (index: number) => Promise<void>;
  handleDownloadAllImages?: () => Promise<void>;
  handleSetAsCover: () => void;
  handleCopyImage: (index: number) => Promise<void>;
  additionalMenuItems?: MenuAction[];
}

const GalleryMenu = ({
  contextMenu,
  selectedFile,
  rightClickedIndex,
  handleContextMenuClose,
  handleDownload,
  handleDownloadAll,
  handleDownloadImage,
  handleDownloadAllImages,
  handleSetAsCover,
  handleCopyImage,
  additionalMenuItems,
}: GalleryMenuProps) => {
  const { t } = useTranslation();
  const workflowRole = useUserWorkflowRole();

  const onDownload = () => {
    if (rightClickedIndex === null) {
      return;
    }

    void handleDownload(rightClickedIndex);
  };

  const onDownloadAll = () => void handleDownloadAll();

  const onDownloadImage = async () => {
    if (rightClickedIndex === null || !handleDownloadImage) {
      return;
    }
    await handleDownloadImage(rightClickedIndex);
  };

  const onDownloadAllImages = async () => handleDownloadAllImages?.();

  const onSetAsCover = () => handleSetAsCover();

  const onCopyImage = () => {
    if (rightClickedIndex === null) {
      return;
    }
    void handleCopyImage(rightClickedIndex);
  };

  const items: MenuAction[] =
    selectedFile?.type === '3D'
      ? [
          {
            type: 'slot',
            el: (
              <ParentMenuItem
                label={t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.DOWNLOAD_IMAGE)}
                items={[
                  {
                    biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_current_image' },
                    name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.CURRENT),
                    action: onDownloadImage,
                    disabled: !handleDownloadImage,
                    withLoading: true,
                  },
                  {
                    biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_all_images' },
                    name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.ALL),
                    action: onDownloadAllImages,
                    disabled: !handleDownloadAllImages,
                    withLoading: true,
                  },
                ]}
                onClose={handleContextMenuClose}
                contextMenuWidth="160px"
              />
            ),
          },
          {
            type: 'slot',
            el: (
              <ParentMenuItem
                label={t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.DOWNLOAD_MODEL)}
                items={[
                  {
                    biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_current_model' },
                    name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.CURRENT),
                    action: onDownload,
                  },
                  {
                    biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_all_models' },
                    name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.ALL),
                    action: onDownloadAll,
                  },
                ]}
                onClose={handleContextMenuClose}
                contextMenuWidth="160px"
              />
            ),
          },
        ]
      : [
          {
            biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_current' },
            name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.DOWNLOAD),
            action: onDownload,
          },
          {
            biEventKey: { menu: MENU_NAMES.GALLERY, action: 'download_all' },
            name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.DOWNLOAD_ALL),
            action: onDownloadAll,
          },
        ];

  if (selectedFile?.type === 'image') {
    if (workflowRole === 'editor') {
      items.push({
        biEventKey: { menu: MENU_NAMES.GALLERY, action: 'set_as_cover' },
        name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.SET_AS_COVER),
        action: onSetAsCover,
      });
    }
    items.push({
      biEventKey: { menu: MENU_NAMES.GALLERY, action: 'copy_image' },
      name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.COPY_IMAGE),
      action: onCopyImage,
    });
  }

  if (additionalMenuItems && additionalMenuItems.length > 0) {
    items.push(...additionalMenuItems);
  }

  return (
    <AppContextMenu
      width="160px"
      items={items}
      open={contextMenu !== null}
      onClose={handleContextMenuClose}
      mouseX={contextMenu?.mouseX}
      mouseY={contextMenu?.mouseY}
    />
  );
};

export default GalleryMenu;
